import React, { useEffect, useState } from "react";
// import { apiFetch } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { LoginCredentials, RegisterCredentials } from "../types/auth";
import "./LoginPage.css";
import Notification from "./Notification";
import OtpInput from "./OtpInput";

const LoginPage: React.FC = () => {
  const { login, requestOTP, isLoading, error, clearError } = useAuth();
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: "",
    otp: "",
  });
  const [showRegister, setShowRegister] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [requestingOTP, setRequestingOTP] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) clearError();
  };

  const handleRequestOTP = async () => {
    if (!credentials.email) {
      return;
    }
    setRequestingOTP(true);
    const success = await requestOTP(credentials.email, "login");
    if (success) {
      setOtpSent(true);
      setOtpCooldown(60);
    }
    setRequestingOTP(false);
  };

  // removed per-digit OTP reset; component handles OTP entry now

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.email || !otp) {
      clearError();
      return;
    }
    const success = await login({ email: credentials.email, otp });
    if (success) {
      setOtpSent(false);
      setOtp("");
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>
            Cab Service
          </h1>
          <p>
            Sign in to continue
          </p>
        </div>


        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email"
              disabled={otpSent}
            />
          </div>

          {/* OTP login */}
            <>
              {!otpSent ? (
                <button
                  type="button"
                  className="login-btn"
                  onClick={handleRequestOTP}
                  disabled={requestingOTP || !credentials.email}
                  style={{ marginBottom: "10px" }}
                >
                  {requestingOTP ? "Sending OTP..." : "Send OTP"}
                </button>
              ) : (
                <>
                  <div className="form-group">
                    <label>Enter OTP</label>
                    <OtpInput
                      value={otp}
                      onChange={(v) => { setOtp(v); if (error) clearError(); }}
                      length={6}
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={async () => {
                      if (otpCooldown > 0) return;
                      setRequestingOTP(true);
                      try {
                        const ok = await requestOTP(credentials.email, "login");
                        if (ok) setOtpCooldown(60);
                      } finally {
                        setRequestingOTP(false);
                      }
                    }}
                    style={{ marginBottom: "10px", fontSize: "12px" }}
                  >
                    {otpCooldown > 0 ? `Resend OTP (${otpCooldown}s)` : "Resend OTP"}
                  </button>
                </>
              )}
            </>
          <button type="submit" className="login-btn" disabled={isLoading || !otpSent}>
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{" "}
            <button
              type="button"
              className="link-btn"
              onClick={() => setShowRegister(true)}
            >
              Sign up
            </button>
          </p>
        </div>

        {/* Instructions
        <div className="demo-credentials">
          <h4>Getting Started</h4>
          <div className="demo-section">
            <p>
              <strong>New Users:</strong> Click "Sign up" to create an account (OTP required)
            </p>
            <p>
              <strong>Existing Users:</strong> Enter your email, request OTP, then enter it to sign in.
            </p>
            <p>
              <strong>Drivers:</strong> Driver accounts are created by admins. Please contact your administrator.
            </p>
          </div>
            <button
            onClick={async () => {
              console.log("Testing API connection...");
              try {
                const response = await apiFetch("/health");
                const data = await response.json();
                console.log("API test result:", data);
                setNotice({ kind: 'success', message: `API Test: ${data.message}` });
              } catch (error) {
                console.error("API test failed:", error);
                setNotice({ kind: 'error', message: `API Test Failed: ${error instanceof Error ? error.message : String(error)}` });
              }
            }}
            style={{
              marginTop: "10px",
              backgroundColor: "var(--primary-teal)",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              fontSize: "12px",
              marginRight: "10px",
            }}
          >
            Test API Connection
          </button>
          <button
            onClick={() => {
              sessionStorage.clear();
              localStorage.clear();
              window.location.reload();
            }}
            style={{
              marginTop: "10px",
              backgroundColor: "var(--error)",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "var(--radius-sm)",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            Clear All Sessions
          </button>
        </div> */}
      </div>

      {notice && (
        <Notification
          kind={notice.kind}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="modal-overlay" onClick={() => setShowRegister(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <RegisterForm onClose={() => setShowRegister(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

// Register Form Component (inline for simplicity)
const IIITD_DOMAIN = "@iiitd.ac.in"; // allowed self-registration domain

const RegisterForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { register, requestOTP, isLoading, error, clearError } = useAuth();
  const [notice, setNotice] = useState<{ kind: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    email: "",
    otp: "",
    name: "",
    role: "rider",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [requestingOTP, setRequestingOTP] = useState(false);
  const [regOtpCooldown, setRegOtpCooldown] = useState(0);

  React.useEffect(() => {
    if (regOtpCooldown <= 0) return;
    const t = setTimeout(() => setRegOtpCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [regOtpCooldown]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const sanitizedValue = value;
    setCredentials((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
    if (error) clearError();
  };

  const handleRequestOTP = async () => {
    if (!credentials.email) return;
    // Frontend domain restriction
    const lower = credentials.email.trim().toLowerCase();
    if (!lower.endsWith(IIITD_DOMAIN)) {
      // Just show info; backend will also enforce
      setRequestingOTP(false);
      return;
    }
    setRequestingOTP(true);
    const success = await requestOTP(credentials.email, "register");
    if (success) {
      setOtpSent(true);
      setRegOtpCooldown(60);
    }
    setRequestingOTP(false);
  };

  // no per-digit OTP functions needed; using OtpInput

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // console.log("Register form submitted with credentials:", credentials);
    
    const registrationData: RegisterCredentials = {
      email: credentials.email,
      otp,
      name: credentials.name,
      role: "rider",
    };
    // console.log("Registration data:", registrationData);

    // console.log("Calling register function...");
    const success = await register(registrationData);
    // console.log("Registration success:", success);

    if (success) {
      setNotice({ kind: 'success', message: 'Account created successfully! You are now logged in.' });
      setOtpSent(false);
      setOtp("");
      // Delay close slightly so user sees confirmation
      setTimeout(() => {
        onClose();
      }, 1200);
    } else {
      setNotice({ kind: 'error', message: 'Registration failed. Please verify OTP and details.' });
    }
  };

  return (
    <div className="register-form">
      {notice && (
        <Notification
          kind={notice.kind}
          message={notice.message}
          onClose={() => setNotice(null)}
        />
      )}
      <div className="register-header">
        <h2>Create Rider Account</h2>
        <button className="close-btn" onClick={onClose}>
          ×
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label htmlFor="name">Full Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={credentials.name}
            onChange={handleInputChange}
            required
            placeholder="Enter your full name"
          />
        </div>

        <div className="form-group" style={{ marginTop: "8px" }}>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={credentials.email}
            onChange={handleInputChange}
            required
            placeholder="Enter your IIITD email"
            disabled={otpSent}
          />
        </div>
        {credentials.email && !credentials.email.trim().toLowerCase().endsWith(IIITD_DOMAIN) && (
          <div className="info-message outside-domain" style={{
            background: '#fff8e1',
            border: '1px solid #ffcc80',
            padding: '8px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            marginBottom: '10px'
          }}>
            <strong style={{ display: 'block', marginBottom: '6px' }}>
              Only IIITD users with an <span className="iiitd-domain">@iiitd.ac.in</span> email can create accounts directly.
            </strong>
            If you are an external user, please fill this form to request an account:{' '}
            <a href="https://forms.gle/iA5YFPadBJfRTDuk9" target="_blank" rel="noopener noreferrer">Request Access</a>. Admin will review and create the account. Once created, you can log in via OTP.
          </div>
        )}

          <>
            {!otpSent ? (
              <button
                type="button"
                className="register-btn"
                onClick={handleRequestOTP}
                disabled={
                  requestingOTP ||
                  !credentials.email ||
                  !credentials.email.trim().toLowerCase().endsWith(IIITD_DOMAIN)
                }
                style={{ marginBottom: "10px" }}
              >
                {requestingOTP ? "Sending OTP..." : "Send OTP"}
              </button>
            ) : (
              <>
                <div className="form-group">
                  <label>Enter OTP</label>
                  <OtpInput
                    value={otp}
                    onChange={(v) => { setOtp(v); if (error) clearError(); }}
                    length={6}
                    disabled={isLoading}
                  />
                </div>
                <button
                  type="button"
                  className="link-btn"
                  onClick={async () => {
                    if (regOtpCooldown > 0) return;
                    setRequestingOTP(true);
                    try {
                      const ok = await requestOTP(credentials.email, "register");
                      if (ok) setRegOtpCooldown(60);
                    } finally {
                      setRequestingOTP(false);
                    }
                  }}
                  style={{ marginBottom: "10px", fontSize: "12px" }}
                >
                  {regOtpCooldown > 0 ? `Resend OTP (${regOtpCooldown}s)` : "Resend OTP"}
                </button>
              </>
            )}
          </>


        <button type="submit" className="register-btn" disabled={isLoading || !otpSent}>
          {isLoading ? "Creating Account..." : "Create Account"}
        </button>

        {isLoading && (
          <div
            style={{
              textAlign: "center",
              marginTop: "10px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            Please wait while we create your account...
          </div>
        )}
      </form>
    </div>
  );
};

export default LoginPage;
